import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Clock } from 'lucide-react';
import { ChefId } from '@/types/chef';
import { getChefLoadingTheme } from '@/config/chefLoadingThemes';

interface StudioGenerationOverlayProps {
  isOpen: boolean;
  personaId?: ChefId | string;
  personaName?: string;
  recipeQuery?: string;
  modelLabel?: string;
}

const EXPECTED_TOTAL_MS = 25000;
const STEP_INTERVAL_MS = 4200;
const TIMER_TICK_MS = 100;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export const StudioGenerationOverlay: React.FC<StudioGenerationOverlayProps> = ({
  isOpen,
  personaId,
  personaName,
  recipeQuery,
  modelLabel,
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  const chefTheme = useMemo(() => getChefLoadingTheme(personaId || ''), [personaId]);
  const steps = useMemo(() => chefTheme.messages, [chefTheme]);

  useEffect(() => {
    if (!isOpen) {
      setElapsedMs(0);
      setCurrentStep(0);
      startTimeRef.current = null;
      return;
    }

    startTimeRef.current = Date.now();

    const tickInterval = setInterval(() => {
      if (startTimeRef.current) {
        setElapsedMs(Date.now() - startTimeRef.current);
      }
    }, TIMER_TICK_MS);

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => {
        const next = prev + 1;
        if (next >= steps.length - 1) return steps.length - 1;
        return next;
      });
    }, STEP_INTERVAL_MS);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      clearInterval(tickInterval);
      clearInterval(stepInterval);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, steps.length]);

  const progressRaw = Math.min(elapsedMs / EXPECTED_TOTAL_MS, 1);
  const progressPct = easeOutCubic(progressRaw) * 100;
  const clampedPct = Math.min(progressPct, 95);

  const isFinalStep = currentStep >= steps.length - 1;
  const isOverestimate = elapsedMs > EXPECTED_TOTAL_MS;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-live="polite"
        >
          {/* Blurred backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
            aria-hidden="true"
          />

          {/* Modal card */}
          <motion.div
            className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-white/10 overflow-hidden"
            initial={{ scale: 0.94, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {/* Top gradient accent strip */}
            <div
              className={`h-1.5 w-full bg-gradient-to-r ${chefTheme.gradient}`}
              aria-hidden="true"
            />

            <div className="px-7 sm:px-9 py-8 sm:py-10">
              {/* Header — chef identity */}
              <div className="flex items-center gap-3 mb-7">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${chefTheme.gradient} shadow-lg`}
                  aria-hidden="true"
                >
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight truncate">
                    {personaName ? personaName : 'Your Chef'}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    is composing your recipe
                  </p>
                </div>
              </div>

              {/* Recipe query */}
              {recipeQuery && (
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-6 truncate">
                  <span className="text-gray-400 dark:text-gray-500">Dish: </span>
                  <span className="font-medium">{recipeQuery}</span>
                </p>
              )}

              {/* Animated phase indicator */}
              <div className="mb-6 min-h-[2.75rem] flex items-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${chefTheme.gradient} text-white font-medium text-sm shadow-md`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse"
                      aria-hidden="true"
                    />
                    {steps[currentStep] || steps[steps.length - 1]}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Eased progress bar */}
              <div className="mb-3">
                <div
                  className="relative h-2 w-full bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(clampedPct)}
                >
                  <motion.div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${chefTheme.gradient} rounded-full`}
                    initial={{ width: 0 }}
                    animate={{ width: `${clampedPct}%` }}
                    transition={{ duration: 0.25, ease: 'linear' }}
                  />
                </div>
              </div>

              {/* Elapsed time + estimate */}
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="tabular-nums font-medium">{formatElapsed(elapsedMs)} elapsed</span>
                </span>
                <span className="tabular-nums">
                  {isOverestimate
                    ? 'Just a moment longer...'
                    : `~${Math.max(1, Math.ceil((EXPECTED_TOTAL_MS - elapsedMs) / 1000))}s remaining`}
                </span>
              </div>

              {/* Model subtext (optional, subtle) */}
              {modelLabel && (
                <p className="mt-5 pt-4 border-t border-gray-100 dark:border-slate-800 text-[11px] text-gray-400 dark:text-gray-500 truncate">
                  Model: <span className="font-mono">{modelLabel}</span>
                </p>
              )}

              {/* Footer disclaimer */}
              <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
                {isFinalStep
                  ? "Almost ready — finishing the last details."
                  : "Keep this tab open. We're generating your recipe in real time."}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StudioGenerationOverlay;